<script setup lang="ts">
import { PriceOverrideStatus } from "@classes-service-types/price-override-status.ts";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import { computed, ref, toRefs, watch } from "vue";
import { modalPresets } from "@/components/OperationsApp/components/Modals/styles.ts";
import { REJECTED_DISPLAY_TEXT } from "@/components/OperationsApp/constants.ts";
import { getPriceDifference } from "@/components/OperationsApp/utils.ts";
import { formatDateUtc } from "@/util/date";
import { formattedPriceFromCents } from "@/util/formatter";
import { detailModalPresets } from "./styles.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "DetailModal",
});

const props = withDefaults(defineProps<Props>(), {});
const { visible, data } = toRefs(props);

const isVisible = ref(false);

const statusText = computed(() =>
  data?.value?.status === PriceOverrideStatus.Approved ? PriceOverrideStatus.Approved : REJECTED_DISPLAY_TEXT,
);

watch(
  visible,
  (newVisible, oldVisible) => {
    if (newVisible !== oldVisible) {
      isVisible.value = !!newVisible;
    }
  },
  { immediate: true },
);
</script>

<template>
  <Button class="w-18 h-10 rounded-none" :pt="detailModalPresets.viewButton" label="View" outlined @click="() => isVisible = true "/>
  <Dialog v-model:visible="isVisible" @hide="() => isVisible = false" modal :dismissable-mask="true" :close-on-escape="true" :pt="modalPresets.dialog">
    <template #header>
      <div class="font-montserrat inline-flex align-items-center justify-content-center">
        <div>{{ statusText }} Details</div>
      </div>
    </template>
    <template #default>
      <div class="grid grid-cols-2 gap-x-4 gap-y-6">
        <div>
          <header class="font-semibold mb-2">Student Name</header>
          <p>{{ data?.participant_fullname || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Session</header>
          <p>{{ data?.name || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Product Type</header>
          <p>{{ data?.product_type || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Requested Amount</header>
          <p>{{ data?.override_amount_in_cents? formattedPriceFromCents(data?.override_amount_in_cents) : "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Difference Amount</header>
          <p>{{ formattedPriceFromCents(getPriceDifference(data)) }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Original Amount</header>
          <p>{{ data?.original_amount_in_cents? formattedPriceFromCents(data?.original_amount_in_cents) : "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">{{ statusText }} By</header>
          <p>{{ data?.decided_by || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">{{ statusText }} Date</header>
          <p>{{data?.decision_date ? formatDateUtc(data?.decision_date) : "" }}</p>
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">{{ statusText }} Notes</header>
          <p>{{ data?.decision_notes || "" }}</p>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
